import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { APP_VERSION } from '../../version';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Small footer marker (admin-only) that shows:
 *  - the FRONTEND build version (baked into the JS bundle at build time)
 *  - the BACKEND version fetched from /api/version
 *  - a button to run the media-link audit (are all photos/videos properly linked?)
 */
export const BuildMarker = () => {
  const [serverVersion, setServerVersion] = useState(null);
  const [error, setError] = useState(null);
  const [audit, setAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_URL}/api/version`, { timeout: 5000 })
      .then((res) => {
        if (!cancelled) setServerVersion(res.data);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || 'unavailable');
      });
    return () => { cancelled = true; };
  }, []);

  const match =
    serverVersion && serverVersion.version && serverVersion.version === APP_VERSION;

  const runAudit = async () => {
    setAuditLoading(true);
    setAudit(null);
    try {
      const res = await axios.get(`${API_URL}/api/admin/audit/media-links`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        timeout: 30000,
      });
      setAudit(res.data);
      if (res.data.total_broken_urls === 0) {
        toast.success('All media URLs are valid S3 links');
      } else {
        toast.error(`${res.data.total_broken_urls} broken URL(s) found`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Audit failed');
    } finally {
      setAuditLoading(false);
    }
  };

  return (
    <div
      className="mt-12 pt-6 border-t border-border/50 text-xs text-muted-foreground"
      data-testid="admin-build-marker"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="space-y-1">
          <div>
            <span className="font-mono font-semibold">Frontend build:</span>{' '}
            <span className="font-mono" data-testid="admin-frontend-version">
              {APP_VERSION}
            </span>
          </div>
          <div>
            <span className="font-mono font-semibold">Backend build:</span>{' '}
            <span className="font-mono" data-testid="admin-backend-version">
              {serverVersion ? serverVersion.version : error ? `unavailable (${error})` : 'loading…'}
            </span>
          </div>
        </div>
        {serverVersion && (
          <div
            className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
              match
                ? 'bg-green-500/15 text-green-700 border border-green-500/30'
                : 'bg-amber-500/15 text-amber-700 border border-amber-500/30'
            }`}
            data-testid="admin-build-match-badge"
          >
            {match ? 'Frontend ↔ Backend in sync' : 'Frontend & Backend out of sync'}
          </div>
        )}
      </div>

      {/* Media Link Audit — check every photo/video row has a valid S3 URL */}
      <div className="mt-4 flex flex-col gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={runAudit}
            disabled={auditLoading}
            data-testid="run-media-audit-btn"
          >
            {auditLoading ? (
              <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Auditing…</>
            ) : (
              'Audit Media Links'
            )}
          </Button>
          <span className="text-[11px] text-muted-foreground/70">
            Scans every photo/video row and verifies its URL points to S3.
          </span>
        </div>

        {audit && (
          <div
            className={`text-xs border rounded-md p-3 space-y-2 ${
              audit.total_broken_urls === 0
                ? 'border-green-500/30 bg-green-500/5'
                : 'border-amber-500/40 bg-amber-500/5'
            }`}
            data-testid="media-audit-result"
          >
            <div className="font-semibold">{audit.verdict}</div>
            <div>
              Scanned <span className="font-mono">{audit.total_rows_scanned}</span> rows across {audit.per_collection.length} collections.
            </div>
            <details className="cursor-pointer">
              <summary className="text-xs">Per-collection breakdown</summary>
              <div className="mt-2 space-y-1 font-mono text-[11px]">
                {audit.per_collection.map((c, i) => (
                  <div key={i} className={c.broken > 0 ? 'text-amber-700' : ''}>
                    {c.broken > 0 ? '⚠' : '✓'} {c.collection}.{c.field}: s3_ok={c.counts.s3_ok}, empty={c.counts.empty}, legacy_uploads={c.counts.legacy_uploads}, other={c.counts.other}
                    {c.broken_samples.length > 0 && (
                      <div className="ml-4 mt-1 text-amber-700">
                        {c.broken_samples.map((s, j) => (
                          <div key={j}>• {s.label} — {JSON.stringify(s[c.field])}</div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </details>
          </div>
        )}
      </div>

      {serverVersion?.build_notes?.length > 0 && (
        <details className="mt-3 cursor-pointer">
          <summary className="text-xs">What&apos;s in this build?</summary>
          <ul className="list-disc list-inside mt-2 space-y-0.5">
            {serverVersion.build_notes.map((n, i) => (
              <li key={i}>{n}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

export default BuildMarker;
